<template>
  <div class="payout-card">
    <div class="payout-icon">
      <i class="fas fa-wallet"></i>
    </div>
    <div class="payout-content">
      <h2 class="payout-title">Total Available Payout</h2>
      <div class="payout-amount">${{ totalOutstandingPayout }}</div>
      <div class="payout-info">
        <span>This month: ${{ payouts }}</span>
        <a @click="togglePayoutHistory" class="history-link">
          <i class="fas fa-history"></i> View Payment History
        </a>
      </div>
      <div class="payout-actions">
        <div class="payout-date">
          <i class="fas fa-calendar-day"></i> Next Payout: {{ payoutDate }}
        </div>
        <button 
          @click="requestPayout" 
          class="payout-button" 
          :disabled="requestedPayout"
        >
          <i class="fas fa-money-check-alt"></i> Request Payout
        </button>
      </div>
      <div v-if="payoutRequestSent" class="confirmation-message">
        <i class="fas fa-check-circle"></i> Request has been sent. Someone will contact you soon.
      </div>
      
      <!-- Payment History Section (shown/hidden with toggle) -->
      <div v-if="showPayoutHistory" class="payment-history">
        <h3>Payment History</h3>
        <div class="history-table-container">
          <table v-if="payoutHistory.length > 0" class="history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(record, index) in payoutHistory" :key="index">
                <td>{{ formatDate(record.date) }}</td>
                <td>${{ record.amount.toFixed(2) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="no-history">No payment history available</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'TotalPayout',
  props: {
    payouts: String,
  },
  data() {
    return {
      payoutDate: this.getPayoutDate(),
      requestedPayout: false,
      payoutRequestSent: false,
      businessId: null,
      totalOutstandingPayout: '0.00',
      showPayoutHistory: false,
      payoutHistory: [],
    };
  },
  mounted() {
    this.loadBusinessId();
    this.checkPayoutStatus();
    this.fetchTotalPayoutAmount();
  },
  methods: {
    getPayoutDate() {
      const date = new Date();
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      return lastDay.toLocaleDateString();
    },
    loadBusinessId() {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.business_id) {
        this.businessId = user.business_id;
      } else {
        console.error('Business ID not found in localStorage');
      }
    },
    async checkPayoutStatus() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/business-sales/salesAmount/${this.businessId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        this.requestedPayout = response.data[0].requested_payout;
      } catch (error) {
        console.error('Error checking payout status:', error);
      }
    },
    async fetchTotalPayoutAmount() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/business-sales/totalPayoutAmount/${this.businessId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        this.totalOutstandingPayout = response.data.totalPayout;
      } catch (error) {
        console.error('Error fetching total payout amount:', error);
      }
    },
    async requestPayout() {
      try {
        await axios.post(`${process.env.VUE_APP_API_URL}/api/business-sales/request-payout/${this.businessId}`, {}, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        this.requestedPayout = true;
        this.payoutRequestSent = true;
      } catch (error) {
        console.error('Error requesting payout:', error);
        alert('Error requesting payout');
      }
    },
    togglePayoutHistory() {
      this.showPayoutHistory = !this.showPayoutHistory;
      if (this.showPayoutHistory) {
        this.fetchPayoutHistory();
      }
    },
    async fetchPayoutHistory() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/business-sales/payout-history/${this.businessId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        this.payoutHistory = response.data || [];
      } catch (error) {
        console.error('Error fetching payout history:', error);
        this.payoutHistory = [];
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
  },
};
</script>

<style scoped>
.payout-card {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  flex: 1;
  transition: transform 0.2s, box-shadow 0.2s;
  height: 100%;
}

.payout-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.payout-icon {
  background-color: #4C6B30;
  color: white;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-right: 1.5rem;
  flex-shrink: 0;
}

.payout-content {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.payout-title {
  font-size: 1.1rem;
  color: #666;
  margin: 0 0 0.5rem 0;
  font-weight: normal;
}

.payout-amount {
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
}

.payout-info {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.75rem;
}

.payout-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
}

.payout-date {
  color: #666;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.payout-button {
  padding: 0.5rem 1rem;
  background-color: #4C6B30;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.payout-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.payout-button:hover:not(:disabled) {
  background-color: #6E9F49;
}

.confirmation-message {
  margin-top: 0.75rem;
  color: #4C6B30;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  padding: 0.5rem;
  background-color: #f0f8eb;
  border-radius: 4px;
}

.history-link {
  color: #4C6B30;
  cursor: pointer;
  font-size: 0.9rem;
  display: block;
  margin-top: 5px;
  text-decoration: underline;
}

.payment-history {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.payment-history h3 {
  font-size: 1rem;
  color: #4C6B30;
  margin-top: 0;
  margin-bottom: 10px;
}

.history-table-container {
  max-height: 200px;
  overflow-y: auto;
}

.history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.history-table th,
.history-table td {
  padding: 8px;
  border-bottom: 1px solid #eee;
  text-align: left;
}

.history-table th {
  font-weight: bold;
  color: #666;
}

.no-history {
  font-style: italic;
  color: #666;
}

/* Media Queries for responsiveness */
@media (max-width: 768px) {
  .payout-card {
    padding: 1.25rem;
  }
  
  .payout-icon {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
    margin-right: 1rem;
  }

  .payout-title {
    font-size: 1rem;
  }

  .payout-amount {
    font-size: 1.5rem;
  }
  
  .payout-actions {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
}

@media (max-width: 480px) {
  .payout-card {
    padding: 1rem;
    flex-direction: column;
  }

  .payout-icon {
    margin-right: 0;
    margin-bottom: 0.8rem;
  }

  .payout-content {
    align-items: center;
    text-align: center;
  }
  
  .payout-actions {
    justify-content: center;
    width: 100%;
  }
  
  .payout-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
